import {
  Badge,
  Button,
  ConfirmDialog,
  ErrorState,
  ListSkeleton,
  SettingsRow,
  SettingsSection,
  toast,
  UiLink,
} from '@nexus/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminError } from '../admin/shared';
import { useModules } from '../modules/context';
import { catalogPagePath } from '../modules/settings';
import { catalogApi, catalogsQuery, useLocale, type CatalogDefaultsResult } from './api';

/** Toast after loading defaults: what was created and what could not be resolved. */
export function useDefaultsToast(): (result: CatalogDefaultsResult) => void {
  const { t } = useTranslation();
  return (result) => {
    const notes = [
      result.unresolved > 0 && t('catalogs.defaults.unresolved', { count: result.unresolved }),
      result.skipped > 0 && t('catalogs.defaults.skipped', { count: result.skipped }),
    ].filter((note): note is string => !!note);
    const description = notes.length ? notes.join(' ') : undefined;
    if (result.created === 0 && result.skipped === 0) {
      toast.success(t('catalogs.defaults.nothing'), { description });
    } else {
      toast.success(t('catalogs.defaults.done', { count: result.created }), { description });
    }
  };
}

/**
 * Catalogs of a module and its submodules the person may maintain, for the settings page of the
 * module. With `kernel` the section lists the statuses and priorities of findings instead, which
 * all modules with findings share. Offers to load the defaults while a catalog is still empty
 * (docs/architecture/catalogs.md).
 */
export function CatalogsSection({
  moduleId,
  kernel = false,
}: {
  moduleId: string;
  kernel?: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const client = useQueryClient();
  const reportError = useAdminError();
  const defaultsToast = useDefaultsToast();
  const manifests = useModules().map((m) => m.manifest);
  const source = kernel ? 'core' : moduleId;
  const query = useQuery(catalogsQuery(source, locale));
  const [confirming, setConfirming] = useState(false);
  const catalogs = (query.data ?? []).filter((c) => c.canManage);
  const loadable = catalogs.some((c) => c.entries === 0 && c.defaults > 0);

  const load = useMutation({
    mutationFn: () => catalogApi.loadDefaults({ module: source, locale }),
    onSuccess: defaultsToast,
    onError: (error) => reportError(error),
    onSettled: () => client.invalidateQueries({ queryKey: ['catalogs'] }),
  });

  // Catalogs of submodules carry the name of their module, as on the settings page.
  const title = (owner: string, label: string) => {
    const manifest = manifests.find((m) => m.id === owner);
    return manifest?.parent === moduleId ? `${t(manifest.name)}: ${t(label)}` : t(label);
  };

  return (
    <>
      <SettingsSection
        title={t(kernel ? 'findings.settings.title' : 'catalogs.title')}
        description={t(kernel ? 'findings.settings.description' : 'catalogs.description')}
        footer={
          loadable ? (
            <Button icon="download" onClick={() => setConfirming(true)}>
              {t('catalogs.defaults.load')}
            </Button>
          ) : undefined
        }
      >
        {query.isError && !query.data ? (
          <ErrorState compact title={t('common.loadFailed')} onRetry={() => void query.refetch()} />
        ) : !query.data ? (
          <ListSkeleton card={false} rows={2} trailing className="py-3" />
        ) : (
          catalogs.map((catalog) => {
            const name = title(catalog.owner, catalog.label);
            return (
              <SettingsRow
                key={catalog.key}
                label={name}
                description={catalog.description ? t(catalog.description) : undefined}
                badge={<Badge>{t('catalogs.count', { count: catalog.entries })}</Badge>}
              >
                <Button asChild size="sm" icon="pencil">
                  <UiLink
                    href={catalogPagePath(moduleId, catalog.key)}
                    aria-label={t('catalogs.edit', { name })}
                  >
                    {t('common.edit')}
                  </UiLink>
                </Button>
              </SettingsRow>
            );
          })
        )}
      </SettingsSection>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('catalogs.defaults.title')}
        description={t('catalogs.defaults.description')}
        confirmLabel={t('catalogs.defaults.confirm')}
        onConfirm={() => load.mutateAsync()}
      />
    </>
  );
}
